import {
  BulletinResource,
  deleteBulletinAttachment,
  deleteBulletinPdf,
  patchBulletinIdAttachmentFilename,
  uploadBulletinAttachment,
  uploadBulletinPdf,
} from "@rubi/api-generated";

import { imageTagGroups } from "@/components/alerts/add-v2/model/imageTags";
import {
  BulletinMediaItem,
  BulletinPdf,
} from "@/components/alerts/add-v2/model/types";
import { IEvidenceFile, IPdfFile } from "@/types";

import { BulletinRow } from "./bulletinRowContext";

/**
 * The media half of persistence, and the one part of this flow that is **not** mocked.
 *
 * Media cannot ride the whole-document autosave: the bytes go to S3 through their own multipart
 * endpoints, and the bulletin row is what owns them. The document save is
 * `bulletinRepository.ts`; these five calls are the other half, and they were real first —
 * which is why the URLs in the form are real and the preview can render what was uploaded.
 *
 * Every endpoint answers with the whole `BulletinResource`, so all five return the same snapshot
 * shape rather than "what changed": the server's list is authoritative for filenames, ordering and
 * URLs, and reconciling a partial response against it would be guesswork.
 */

/**
 * What one attachment can be told about itself. Absent fields are left alone by the endpoint.
 *
 * No `entityIds`: the relation moved onto the document PATCH when it became a foreign key — see
 * decision 46. What is left here is what the file owns rather than what the document does.
 */
export interface AttachmentPatch {
  sortOrdinal?: number;
  description?: string;
}

export interface MediaSnapshot {
  media: BulletinMediaItem[];
  pdf: BulletinPdf | null;
}

export interface BulletinMediaRepository {
  uploadMedia: (file: File) => Promise<MediaSnapshot>;
  deleteMedia: (filename: string) => Promise<MediaSnapshot>;
  /**
   * Changes one file's own fields — which entities it shows, where it sits in the order — through the
   * endpoint the old flow already used for `sort_ordinal`. Both are optional server-side, so sending
   * one cannot disturb the other. The response is the whole bulletin, as ever.
   */
  patchAttachment: (
    filename: string,
    patch: AttachmentPatch,
  ) => Promise<MediaSnapshot>;
  uploadPdf: (file: File) => Promise<MediaSnapshot>;
  deletePdf: () => Promise<MediaSnapshot>;
}

/**
 * `BulletinResource` types both of these as `{ [key: string]: unknown }` — FastAPI has no model for
 * either column — so the shapes come from the portal's own `IEvidenceFile` / `IPdfFile`, which are
 * what `../add/` reads the same fields through.
 *
 * Exported because loading a bulletin needs the same reading (`hooks/useBulletinSource.ts`): the five
 * endpoints here and `GET /bulletin/{id}` all answer with the same `BulletinResource`, so there is
 * one place that knows how to read its media and it is this one.
 */
export const toMediaSnapshot = (resource: BulletinResource): MediaSnapshot => {
  const files = Object.values(resource.evidence_files) as IEvidenceFile[];
  const pdf = resource.pdf as Partial<IPdfFile>;

  return {
    media: files
      .map((file) => ({
        // The backend keys attachments by filename and renames collisions on upload, so it is unique
        // within a bulletin and stable — no client id needs to be invented or carried.
        id: file.filename,
        filename: file.filename,
        contentType: file.content_type,
        url: file.download_url,
        thumbnailUrl: file.thumbnail_url ?? null,
        sortOrdinal: file.sort_ordinal ?? 0,
        // Both server-side since 2026-08-19: `entity_ids` through the join table, `description` as
        // a column of its own — see decisions 43 and 46.
        entityIds: file.entity_ids ?? [],
        description: file.description ?? "",
        // Generated on upload, inside that request — by the time a response mentions a file, its
        // tags are final. Which is why there is no "generating…" state here, unlike the prototype.
        tagGroups: imageTagGroups(file.openai_description),
      }))
      .sort((a, b) => a.sortOrdinal - b.sortOrdinal),
    pdf: pdf.filename
      ? {
          filename: pdf.filename,
          contentType: pdf.content_type ?? "application/pdf",
          url: pdf.download_url ?? "",
        }
      : null,
  };
};

const unwrap = async (
  request: Promise<{ data?: unknown; error?: unknown }>,
): Promise<MediaSnapshot> => {
  const { data, error } = await request;
  if (!data) throw error ?? new Error("Bulletin media request failed");
  return toMediaSnapshot(data as BulletinResource);
};

/**
 * Carries forward the fields an attachment response is not the authority on.
 *
 * The rule, once the relation became a foreign key: **what the document owns, the client keeps; what
 * the attachment endpoints own, the server dictates.** Filenames, ordering and URLs are theirs.
 * `entityIds` and `description` ride the document (decision 46), so an upload or a reorder answering
 * with the last-saved links must not undo an edit the autosave has not sent yet.
 */
export const mergeMediaSnapshot = (
  previous: BulletinMediaItem[],
  next: BulletinMediaItem[],
): BulletinMediaItem[] =>
  next.map((item) => {
    const existing = previous.find((row) => row.filename === item.filename);
    return existing
      ? {
          ...item,
          entityIds: existing.entityIds,
          description: existing.description,
        }
      : item;
  });

/** Deleting never creates: there is nothing to delete from a bulletin that does not exist yet. */
const existingId = (row: BulletinRow): number => {
  const id = row.id();
  if (id === null) throw new Error("The bulletin has no media to remove");
  return id;
};

export const createBulletinMediaRepository = (
  row: BulletinRow,
): BulletinMediaRepository => ({
  uploadMedia: async (file) =>
    unwrap(
      uploadBulletinAttachment({
        path: { bulletin_id: await row.ensureId() },
        body: { file },
      }),
    ),

  deleteMedia: (filename) =>
    unwrap(
      deleteBulletinAttachment({
        path: { bulletin_id: existingId(row), filename },
      }),
    ),

  patchAttachment: (filename, patch) =>
    unwrap(
      patchBulletinIdAttachmentFilename({
        path: { bulletin_id: existingId(row), filename },
        body: {
          ...(patch.sortOrdinal !== undefined
            ? { sort_ordinal: patch.sortOrdinal }
            : {}),
          ...(patch.description !== undefined
            ? { description: patch.description }
            : {}),
        },
      }),
    ),

  uploadPdf: async (file) =>
    unwrap(
      uploadBulletinPdf({
        path: { bulletin_id: await row.ensureId() },
        body: { file },
      }),
    ),

  deletePdf: () =>
    unwrap(deleteBulletinPdf({ path: { bulletin_id: existingId(row) } })),
});
